import type { AlertaDeSaude, LinhaDeFila, SaudeDetalhada } from '@arena/shared/schemas';
import type { Situacao } from '../ui/primitivos.jsx';

/**
 * Os textos da tela do operador. Funções puras, testadas em
 * `formatos.test.ts`.
 *
 * Nada aqui passa pelo fuso de uma arena: a fila e o processo são de
 * todas elas. Instantes aparecem RELATIVOS ("há 3 min") — é a pergunta
 * de quem está olhando um incidente —, com o instante exato no `title`.
 */

/** O que cada alerta pede de quem está de plantão. */
export const ROTULO_DO_ALERTA: Readonly<Record<AlertaDeSaude, string>> = {
  banco_indisponivel: 'O banco não respondeu. Veja "Banco lento ou fora" no runbook.',
  migrations_pendentes:
    'Há migration na imagem que o banco não tem: o deploy pulou o migrate deploy.',
  migration_com_falha:
    'Uma migration começou e não terminou. O próximo deploy vai recusar até alguém resolver.',
  migrations_ilegiveis:
    'Não deu para comparar as migrations da imagem com as do banco — e sem comparar não há como afirmar que está tudo certo.',
  fila_indisponivel: 'A fila de jobs não respondeu.',
  fila_morta_com_jobs:
    'Há job na fila morta: trabalho que não aconteceu. Veja a causa abaixo antes de reprocessar.',
};

/** "850 ms", "1,2 s", "2 min 5 s" — e o travessão quando não houve medida. */
export function duracao(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${decimal(ms / 1000)} s`;
  const minutos = Math.floor(ms / 60_000);
  const segundos = Math.round((ms % 60_000) / 1000);
  return segundos > 0 ? `${minutos} min ${segundos} s` : `${minutos} min`;
}

/** 0,0374 → "3,7%". Zero é "0%", sem casa: a coluna inteira costuma ser zero. */
export function percentual(taxa: number): string {
  if (taxa === 0) return '0%';
  return `${decimal(taxa * 100)}%`;
}

/** "12 min", "5 h 12 min", "3 d 4 h" — quanto a réplica está no ar. */
export function tempoNoAr(segundos: number): string {
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `${horas} h ${minutos % 60} min`;
  return `${Math.floor(horas / 24)} d ${horas % 24} h`;
}

/** "agora", "há 3 min", "há 5 h", "há 2 d" — e o travessão para o que nunca aconteceu. */
export function haQuanto(instante: string | null, agora: Date): string {
  if (!instante) return '—';
  const minutos = Math.floor((agora.getTime() - Date.parse(instante)) / 60_000);
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  return `há ${Math.floor(horas / 24)} d`;
}

/** A pílula do topo: tudo certo, ou quantas coisas pedem ação. */
export function situacaoDaSaude(saude: SaudeDetalhada): { situacao: Situacao; rotulo: string } {
  if (saude.ok) return { situacao: 'ok', rotulo: 'Tudo em ordem' };
  const n = saude.alertas.length;
  return { situacao: 'atrasada', rotulo: n === 1 ? '1 alerta' : `${n} alertas` };
}

/**
 * A pílula de uma fila. Falha na janela pesa mais que acúmulo: o job que
 * falhou já virou fila morta; o acumulado ainda pode andar.
 */
export function situacaoDaFila(linha: LinhaDeFila): { situacao: Situacao; rotulo: string } {
  if (linha.falhos > 0) return { situacao: 'atrasada', rotulo: 'Com falha' };
  if (linha.emRetentativa > 0) return { situacao: 'atencao', rotulo: 'Em retentativa' };
  if (linha.ativos > 0) return { situacao: 'aguardando', rotulo: 'Rodando' };
  if (linha.concluidos === 0 && linha.naFila === 0) return { situacao: 'neutra', rotulo: 'Parada' };
  return { situacao: 'ok', rotulo: 'Em dia' };
}

/** O id longo cortado para a tabela; o inteiro vai no `title`, para copiar. */
export function idCurto(id: string | null): string {
  if (!id) return '—';
  return id.length > 13 ? `${id.slice(0, 8)}…` : id;
}

function decimal(valor: number): string {
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}
